import { useState, useCallback } from 'react';

import { useGetBoardData } from 'business/quality-control/pages/record-segment-defect/hooks/data/get-board';
import { useGetDefectIndicatorData } from 'business/quality-control/pages/record-segment-defect/hooks/data/get-defect-indicator';
import { useGetDefectReportTypeData } from 'business/quality-control/pages/record-segment-defect/hooks/data/get-defect-report-type';
import { useGetTunnelPositionData } from 'business/quality-control/pages/record-segment-defect/hooks/data/get-tunnel-position';
import { defectIndicatorSyncService } from 'technical/db/sync/defect-indicator-sync.service';
import { defectReportTypeSyncService } from 'technical/db/sync/defect-report-type-sync.service';
import { segmentDrawConfigSyncService } from 'technical/db/sync/segment-draw-config-sync.service';
import { tunnelPositionSyncService } from 'technical/db/sync/tunnel-position-sync.service';
import { insertOrUpdateLocalStorageValue } from 'technical/local-storage';
import {
  BOARD_CONFIG_KEY,
  QUALITY_SCOPE,
} from 'technical/local-storage/constants';
import { BoardConfigSchema } from 'technical/local-storage/types';

export const useSyncBoardConfigData = (constructionSiteId: string) => {
  const { refetch: refetchBoard, isLoading: isBoardLoading } =
    useGetBoardData(constructionSiteId);
  const { refetch: refetchPositions, isLoading: isTunnelPositionLoading } =
    useGetTunnelPositionData(constructionSiteId);
  const { refetch: refetchReportTypes, isLoading: isDefectReportTypeLoading } =
    useGetDefectReportTypeData(constructionSiteId);
  const { refetch: refetchIndicators, isLoading: isDefectIndicatorLoading } =
    useGetDefectIndicatorData(constructionSiteId);

  const [isSavingToDexie, setIsSavingToDexie] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const isSyncing =
    isBoardLoading ||
    isTunnelPositionLoading ||
    isDefectReportTypeLoading ||
    isDefectIndicatorLoading ||
    isSavingToDexie;

  const handleSync = useCallback(async () => {
    setError(null);

    try {
      const [boardResult, positionResult, reportTypeResult, indicatorResult] =
        await Promise.all([
          refetchBoard(),
          refetchPositions(),
          refetchReportTypes(),
          refetchIndicators(),
        ]);

      const boardData = boardResult.data;
      const tunnelPositionData = positionResult.data;
      const defectReportTypeData = reportTypeResult.data;
      const indicatorData = indicatorResult.data;

      if (
        !boardData ||
        !tunnelPositionData ||
        !defectReportTypeData ||
        !indicatorData
      ) {
        throw new Error('Unable to get board data');
      }

      setIsSavingToDexie(true);

      await Promise.all([
        segmentDrawConfigSyncService.reset(),
        tunnelPositionSyncService.reset(),
        defectReportTypeSyncService.reset(),
        defectIndicatorSyncService.reset(),
      ]);

      insertOrUpdateLocalStorageValue<BoardConfigSchema>({
        scope: QUALITY_SCOPE,
        key: BOARD_CONFIG_KEY,
        value: {
          segmentWidth: boardData.segmentWidth,
        },
      });

      await Promise.all([
        segmentDrawConfigSyncService.download(boardData.segmentConfigs),
        tunnelPositionSyncService.download(tunnelPositionData.positions),
        defectReportTypeSyncService.download(
          defectReportTypeData.defectReportTypes,
        ),
        defectIndicatorSyncService.download(indicatorData.indicators),
      ]);

      return { success: true };
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Technical error'));

      return { success: false };
    } finally {
      setIsSavingToDexie(false);
    }
  }, [refetchBoard, refetchPositions, refetchReportTypes, refetchIndicators]);

  return {
    handleSync,
    isSyncing,
    error,
  };
};
